import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import { supabase } from '@/lib/supabase';

interface RemoteKeyboardContextValue {
  sessionToken: string | null;
  isConnected: boolean;
  isModalOpen: boolean;
  qrCodeUrl: string;
  connectionToast: string | null;
  openQRModal: () => void;
  closeQRModal: () => void;
  closeSession: () => void;
}

const RemoteKeyboardContext = createContext<RemoteKeyboardContextValue | null>(null);

function insertTextAtCursor(text: string): boolean {
  const el = document.activeElement;
  if (!el) return false;

  const tagName = el.tagName.toLowerCase();
  const isEditable =
    tagName === 'input' ||
    tagName === 'textarea' ||
    (el as HTMLElement).isContentEditable;
  if (!isEditable) return false;

  if (el instanceof HTMLInputElement) {
    const skipTypes = ['checkbox', 'radio', 'file', 'submit', 'reset', 'button', 'image', 'hidden'];
    if (skipTypes.includes(el.type)) return false;
  }

  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const original = el.value;
    el.value = original.slice(0, start) + text + original.slice(end);
    const newPos = start + text.length;
    el.selectionStart = newPos;
    el.selectionEnd = newPos;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    sel.removeAllRanges();
    sel.addRange(range);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  return false;
}

export function RemoteKeyboardProvider({ children }: { children: React.ReactNode }) {
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connectionToast, setConnectionToast] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isConnectedRef = useRef(false);

  const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '') || '';

  // Le QR code est généré localement (lib "qrcode") plutôt que via une API tierce :
  // le jeton de session de pairage ne doit pas transiter par un service externe.
  useEffect(() => {
    if (!sessionToken) {
      setQrCodeUrl('');
      return;
    }
    let cancelled = false;
    const pairingUrl = `${window.location.origin}${basePath}/telecommande-clavier?token=${sessionToken}`;
    QRCode.toDataURL(pairingUrl, {
      width: 280,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((dataUrl) => {
        if (!cancelled) setQrCodeUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrCodeUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [sessionToken, basePath]);

  const createSession = useCallback(async () => {
    try {
      const token = crypto.randomUUID();
      const { error } = await supabase.from('remote_keyboard_sessions').insert({
        session_token: token,
        is_connected: false,
        last_keystroke: '',
        keystroke_seq: 0,
      });

      if (error) throw error;

      setSessionToken(token);
      setIsModalOpen(true);
      setIsConnected(false);
      isConnectedRef.current = false;

      // Nettoyer l'ancien canal s'il existe
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      // Créer le canal Realtime : Broadcast pour le texte envoyé en bloc + Postgres changes pour le statut de connexion
      channelRef.current = supabase
        .channel(`rk-${token}`, {
          config: { broadcast: { self: false } },
        })
        .on(
          'broadcast',
          { event: 'text' },
          ({ payload }: { payload: { text: string } }) => {
            if (payload && typeof payload.text === 'string' && payload.text.length > 0) {
              insertTextAtCursor(payload.text);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'remote_keyboard_sessions',
            filter: `session_token=eq.${token}`,
          },
          (payload) => {
            const row = payload.new as { is_connected: boolean } | null;
            if (!row) return;

            if (row.is_connected && !isConnectedRef.current) {
              isConnectedRef.current = true;
              setIsConnected(true);
              setConnectionToast('Connexion réussie');
              setTimeout(() => setConnectionToast(null), 2500);
            }
          }
        )
        .subscribe();
    } catch (err) {
      console.error('[RemoteKeyboard] Session creation failed:', err);
    }
  }, []);

  const closeSession = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    setIsModalOpen(false);
    setIsConnected(false);
    setSessionToken(null);
    isConnectedRef.current = false;
    setConnectionToast(null);
  }, []);

  const openQRModal = useCallback(() => {
    if (sessionToken) {
      setIsModalOpen(true);
    } else {
      createSession();
    }
  }, [sessionToken, createSession]);

  const closeQRModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);

  return (
    <RemoteKeyboardContext.Provider
      value={{
        sessionToken,
        isConnected,
        isModalOpen,
        qrCodeUrl,
        connectionToast,
        openQRModal,
        closeQRModal,
        closeSession,
      }}
    >
      {children}
    </RemoteKeyboardContext.Provider>
  );
}

export function useRemoteKeyboard() {
  const ctx = useContext(RemoteKeyboardContext);
  if (!ctx) {
    throw new Error('useRemoteKeyboard must be used within RemoteKeyboardProvider');
  }
  return ctx;
}